import { Box3, MathUtils, Mesh, Object3D, SkinnedMesh, Vector3 } from "three";
import { IDamageable } from "../IDamageable";
import { ICollidable } from "../../../../../three/physics/ICollidable";
import { PhysicsLayers } from "../../../../../PhysicsLayers";
import { AudioController } from "../../AudioController";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { GameResourcesPackage } from "../../../resources/GameResourcesPackage";
import { CharacterAnimationController, CharacterAnimationState } from "./animations/CharacterAnimationController";
import { Physics } from "../../../../../three/physics/Physics";
import { WeaponType } from "../../../models/data/WeaponType";
import { CharacterProjectile } from "../projectiles/character/CharacterProjectile";
import { Boomerang } from "../projectiles/character/Boomerang";
import { IProjectileContainer } from "../gamefield/IProjectileContainer";
import { GameViewModel } from "../../../models/GameViewModel";
import { BatchedRenderer } from "three.quarks";
import { ICharacterState } from "./states/ICharacterState";
import { DieState } from "./states/DieState";
import { IdleState } from "./states/IdleState";
import { ThrowState } from "./states/ThrowState";
import { Ticker } from "pixi.js";
import { PlayerModel } from "../../../../../common/models/PlayerModel";
import { nameof, NotifyProperty } from "bindable-data";
import { BindableObject3D } from "../../../../../common/utils/BindableObject3D";
import { LevelModel } from "../../../../../common/models/LevelModel";
import { Dynamite } from "../projectiles/character/Dynamite";
import { SaveModel } from "../../../../../common/models/SaveModel";
import { NotifyMap } from "../../../../../notifyproperties/NotifyMap";
import { HitState } from "./states/HitState";
import { CharacterStates } from "./states/CharacterStates";

export class Character extends BindableObject3D implements ICollidable, IDamageable {
    public readonly isDamageable: boolean = true;
    public readonly health: NotifyProperty<number>;
    public readonly currentWeapon: NotifyProperty<WeaponType>;
    public readonly weapons: NotifyMap<WeaponType, number>;

    private readonly states: Map<CharacterStates, ICharacterState>;
    private currentState: [CharacterStates, ICharacterState];

    private readonly characterObject: Object3D;
    private readonly characterHead: Object3D;
    private readonly characterMesh: SkinnedMesh;
    private readonly animationController: CharacterAnimationController;
    private readonly _boundingBox: Box3;
    private weaponObject: CharacterProjectile;
    public throwTargetPosition: Vector3;

    public get alive(): boolean {
        return this.health.value > 0;
    }

    public get bodyPosition(): Vector3 {
        return new Box3().setFromObject(this.characterMesh).getCenter(new Vector3());
    }

    public get boundingBox(): Box3 {
        return this._boundingBox.copy(this.characterMesh.boundingBox).applyMatrix4(this.matrixWorld);
    }

    public get collidesWithLayer(): number {
        return 1 << PhysicsLayers.Layer3;
    }

    public get headPosition(): Vector3 {
        return this.characterHead.getWorldPosition(new Vector3());
    }

    private hitDamageLimit: number;

    public get isHitReadyDamage(): boolean {
        return this.hitDamageLimit === 0;
    }

    private lastTimeShowHitTimeoutMS: number;

    public get isHitReadyTime(): boolean {
        return this.lastTimeShowHitTimeoutMS === 0;
    }

    public constructor(
        private readonly gameResources: GameResourcesPackage,
        private readonly gameViewModel: GameViewModel,
        private readonly levelModel: LevelModel,
        private readonly saveModel: SaveModel,
        private readonly playerModel: PlayerModel,
        private readonly audioController: AudioController,
        private readonly projectileContainer: IProjectileContainer,
        private readonly particlesBatchRenderer: BatchedRenderer
    ) {
        super();

        const defaultWeapons: [WeaponType, number][] = [
            [WeaponType.Rock, -1],
            [WeaponType.Boomerang, 0],
            [WeaponType.Dynamite, 0]
        ];
        this.weapons = new NotifyMap<WeaponType, number>(this, nameof(this, "weapons"), this.saveModel.read("Character", "weapons", "map", defaultWeapons));

        this._boundingBox = new Box3();
        this.characterObject = clone(this.gameResources.assets_game_FarmerSimon_glb.scene.children[0]);
        this.characterMesh = this.characterObject.children[0] as SkinnedMesh;

        this.characterHead = this.characterObject.getObjectByName("Head");

        this.characterMesh.castShadow = true;

        this.animationController = new CharacterAnimationController(this.characterMesh, this.gameResources.assets_game_FarmerSimon_glb.animations);
        
        this.health = new NotifyProperty<number>(this, nameof(this, "health"), levelModel.levelConfiguration.characterHealth);
        this.hitDamageLimit = this.levelModel.levelConfiguration.characterHealth * 0.2;
        this.lastTimeShowHitTimeoutMS = 4000;

        this.currentWeapon = new NotifyProperty(this, nameof(this, "currentWeapon"), WeaponType.Rock);

        this.states = new Map<CharacterStates, ICharacterState>([
            [CharacterStates.DIE, new DieState(this, this.animationController)],
            [CharacterStates.HIT, new HitState(this, this.animationController, audioController)],
            [CharacterStates.IDLE, new IdleState(this, this.animationController, audioController)],
            [CharacterStates.THROW, new ThrowState(this, this.animationController, audioController)]
        ]);

        this.currentState = [CharacterStates.IDLE, this.states.get(CharacterStates.IDLE)];
        this.currentState[1].enter();

        this.add(this.characterObject);

        Ticker.shared.add(this.onTick, this);
        Physics.add(this);

        this.characterMesh.computeBoundingBox();
        this.characterMesh.computeBoundingSphere();

        this.playerModel.applyHealth.add(this.onApplyHealth, this);
        this.playerModel.applyWeapon.add(this.addWeapon, this);
        this.playerModel.resurrectCharacter.add(this.onResurrect, this);

        this.equipProjectile();
    }

    public destroy(): void {
        Ticker.shared.remove(this.onTick, this);
        Physics.remove(this);
        this.playerModel.applyHealth.remove(this.onApplyHealth, this);
        this.playerModel.applyWeapon.remove(this.addWeapon, this);
        this.playerModel.resurrectCharacter.remove(this.onResurrect, this);
        this.animationController.dispose();
    }

    public throw(targetWorldPosition: Vector3): void {
        this.currentState[1].onInput(targetWorldPosition);
    }

    private onApplyHealth(hp: number): void {
        this.health.setValue(MathUtils.clamp(this.health.value + hp, this.health.value, this.levelModel.levelConfiguration.characterHealth));
    }

    public equipProjectile(): void {
        if (this.weaponObject != null) {
            this.weaponObject.removeFromParent();
            this.weaponObject.destroy();
            this.weaponObject = null;
        }
        switch (this.currentWeapon.value) {
            case WeaponType.Rock:
                const rockPlaceholder = this.characterObject.getObjectByName("Throwing(BoomerangStone)Position");
                this.weaponObject = new CharacterProjectile(this.gameResources, this.particlesBatchRenderer, this.projectileContainer, clone(this.gameResources.assets_game_Weapons_glb.scene.children[1]) as Mesh, 0.5, 100);
                rockPlaceholder.add(this.weaponObject);
                this.weaponObject.updateMatrixWorld(true);
                break;
            case WeaponType.Boomerang:
                const boomerangPlaceholder = this.characterObject.getObjectByName("Throwing(BoomerangStone)Position");
                this.weaponObject = new Boomerang(this.gameResources, this.particlesBatchRenderer, this.projectileContainer, this.gameViewModel, clone(this.gameResources.assets_game_Weapons_glb.scene.children[5]) as Mesh, 0.5);
                boomerangPlaceholder.add(this.weaponObject);
                this.weaponObject.updateMatrixWorld(true);
                break;
            case WeaponType.Dynamite:
                const dynamitePlaceholder = this.characterObject.getObjectByName("Throwing(LightningFreeze)Position");
                this.weaponObject = new Dynamite(this.projectileContainer, this.gameResources, this.audioController, this.particlesBatchRenderer, this.gameViewModel, clone(this.gameResources.assets_game_Weapons_glb.scene.children[4]) as Mesh, 0.3);
                dynamitePlaceholder.add(this.weaponObject);
                this.weaponObject.updateMatrixWorld(true);
                break;
            default:
                console.warn(`Attempt to set unsupported character weapon: ${this.currentWeapon}`);
        }
    }

    public tryThrowProjectile(): void {
        if (this.throwTargetPosition == null || this.weaponObject == null) {
            return;
        }
        this.weaponObject.throwToPoint(this.throwTargetPosition);
        this.weaponObject = null;
        const subtractResult = this.subtractWeapon(this.currentWeapon.value);
        if (subtractResult) {
            this.changeWeapon(WeaponType.Rock);
        }
    }

    public takeDamage(amount: number): void {
        this.currentState[1].onTakeDamage(amount);
        this.hitDamageLimit = Math.max(this.hitDamageLimit - amount, 0);
    }

    public async playVictoryAnimation(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.animationController.onClipEnd.once(() => resolve(), this);
            this.animationController.transitToState(CharacterAnimationState.Victory);
        });
    }

    public async playLoseAnimation(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.animationController.onClipEnd.once(() => resolve(), this);
            this.animationController.transitToState(CharacterAnimationState.Defeat);
        });
    }

    public async deathAnimationEnd(): Promise<void> {
        return new Promise<void>((resolve) => {
            this.animationController.onClipEnd.once(() => resolve(), this);
        });
    }

    public changeWeapon(weapon: WeaponType): void {
        this.currentWeapon.setValue(weapon);
        this.equipProjectile();
    }

    public addWeapon(weapon: WeaponType, ammo: number): void {
        if (weapon === WeaponType.Rock) {
            return;
        }
        const currentAmmo = this.weapons.get(weapon);
        this.weapons.set(weapon, currentAmmo + ammo);
        this.saveModel.write("Character", "weapons", "map", this.weapons);
    }

    /**
      * Returns should we or not change weapon.
      *
      * @param weapon - The WeaponType for subtract
      * @returns `boolean` value means should we switch weapon
      *
      */
    public subtractWeapon(weapon: WeaponType): boolean {
        if (weapon === WeaponType.Rock) {
            return false
        }
        const count = this.weapons.get(weapon);
        if (count == 0) {
            return true;
        }
        const newCount = count - 1;
        this.weapons.set(weapon, newCount);
        this.saveModel.write("Character", "weapons", "map", this.weapons);

        return newCount == 0;
    }

    public onResurrect(): void {
        this.resetHitTimeout();
        this.resetDamageLimit();
        this.health.setValue(this.levelModel.levelConfiguration.characterHealth * 0.3);
        this.equipProjectile();
    }

    public resetHitTimeout(): void {
        this.lastTimeShowHitTimeoutMS = 4000;
    }

    public resetDamageLimit(): void {
        this.hitDamageLimit = this.levelModel.levelConfiguration.characterHealth * 0.2;
    }

    private switchState(nextState: CharacterStates): void {
        this.currentState[1]?.exit();
        this.currentState = [nextState, this.states.get(nextState)];
        this.currentState[1].enter();
    }

    private onTick(_dt: number): void {
        this.lastTimeShowHitTimeoutMS = Math.max(this.lastTimeShowHitTimeoutMS - Ticker.shared.deltaMS, 0);
        const [stateType, state] = this.currentState;
        const nextState = state.process(Ticker.shared.deltaMS);
        if (nextState != stateType) {
            this.switchState(nextState);
        }
    }
}
